import { Injectable } from '@angular/core';
import { SecurityPermissionName } from '@app/core/models/permission';
import { CommandDefinition, CommandHelper, QuickCommandService } from '@app/shared/quick-commands';
import {
  AsyncCommandOption,
  CommandOption,
  CommandOptionFactory,
  OptionResult,
} from '@app/shared/quick-commands/command-option-factory';
import { ServiceProviderContextService } from '@app/shared/services/context/service-provider-context.service';
import { sortBy } from '@app/utils/sort/sort-by';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SyncCommand implements CommandDefinition<any> {
  authorization = {
    allPermissions: [SecurityPermissionName.MANAGE_SYSTEM],
  };
  icon = 'far fa-undo';
  label = 'quickCommands.commands.sync';
  name = 'sync';
  section = QuickCommandService.DevSection;

  private optionFactory = new CommandOptionFactory('sync');
  private possibleOptions = this.getOptions();
  private enterpriseName: AsyncCommandOption<OptionResult>;
  private enterprisePaths: CommandOption<OptionResult>;
  private providerName: CommandOption<OptionResult>;
  private providerPaths: CommandOption<OptionResult>;
  private typeOption: CommandOption<OptionResult>;

  constructor(private serviceProviderContextService: ServiceProviderContextService) {
    this.enterpriseName = this.optionFactory.list$('enterprise', (query) =>
      this.serviceProviderContextService.enterprises.data$.pipe(
        map((enterprises) => {
          return enterprises
            .slice()
            .sort(sortBy([['name', 'asc']]))
            .map((enterprise) => enterprise.name)
            .filter((name) => name.toLowerCase().includes(query.toLowerCase()));
        })
      )
    );
    this.enterprisePaths = this.optionFactory.list('path', Object.keys(this.possibleOptions.enterprise), {
      sort: 'asc',
    });

    this.providerName = this.optionFactory.input('provider');
    this.providerPaths = this.optionFactory.list('path', Object.keys(this.possibleOptions.serviceProvider), {
      sort: 'asc',
    });

    this.typeOption = this.optionFactory.list('type', ['enterprise', 'serviceProvider']);
  }

  action(query: string, [name, path, ...queryParameters]: OptionResult[]) {
    const type: OptionResult = {
      name: 'type',
      value: 'enterprise',
    };

    if (path != null && name != null) {
      const noDryRunResult = queryParameters.find((result) => result.name === 'noDryRunWarning');

      if (noDryRunResult != null && noDryRunResult.value === 'false') {
        return CommandHelper.return();
      }

      const options: CommandOption<unknown>[] = this.possibleOptions[type.value][path.value];
      const nextOption = options[queryParameters.length];

      if (nextOption != null) {
        return nextOption(query);
      } else {
        this.openPopup(type.value, name.value, path.value, queryParameters);

        return CommandHelper.close();
      }
    }

    if (name != null) {
      return type.value === 'enterprise' ? this.enterprisePaths(query) : this.providerPaths(query);
    }

    return this.enterpriseName(query);
  }

  private getOptions() {
    const DryRun = this.optionFactory.list('dryrun', ['true', 'false']);
    const Force = this.optionFactory.list('force', ['true', 'false']);
    const Manual = this.optionFactory.list('manual', ['true', 'false']);
    const NoDryRunWarning = this.optionFactory.list('noDryRunWarning', ['true', 'false'], {
      warningMessage: 'quickCommands.commands.sync.option.noDryRunWarning.warning',
    });
    const OptionPattern = this.optionFactory.input('optionPattern');
    const Pattern = this.optionFactory.input('pattern', { highlight: false });
    const WithUsers = this.optionFactory.list('withUsers', ['true', 'false']);

    return {
      enterprise: {
        enterprise: [DryRun],
        data: [DryRun],
        options: [Manual, OptionPattern, DryRun],
        recalculateUsage: [DryRun],
        resetUserFeatures: [NoDryRunWarning],
        activateUserOptionalFeatures: [Manual, DryRun],
        optionalFeatures: [Manual, DryRun],
        defaultLocation: [DryRun],
        billing: [WithUsers, Force, DryRun],
      },
      serviceProvider: {
        enterprises: [Pattern, DryRun],
        enterpriseData: [Pattern, DryRun],
        enterpriseOptions: [Pattern, Manual, OptionPattern, DryRun],
        recalculateUsage: [Pattern, DryRun],
        resetUserFeatures: [Pattern],
        activateUserOptionalFeatures: [Pattern, Manual, DryRun],
        optionalFeatures: [Pattern, Manual, DryRun],
        enterpriseDefaultLocations: [Pattern, DryRun],
      },
    };
  }

  private openPopup(type: string, name: string, path: string, queryParameters: OptionResult[]): void {
    const query = queryParameters
      .filter((result) => result.name !== 'noDryRunWarning')
      .map((result) => `${result.name}=${result.value}`)
      .join('&');

    if (path === 'enterprise') {
      path = '';
    } else {
      path = `/${path}`;
    }

    if (type === 'enterprise') {
      window.open(
        `${window.location.protocol}//${window.location.host}/middleware/api/sync/enterprise/${name}${path}?${query}`,
        'syncMiddleware',
        'popup,height=600,width=800'
      );
    } else {
      window.open(
        `${window.location.protocol}//${window.location.host}/middleware/api/sync/provider/${name}${path}?${query}`,
        'syncMiddleware',
        'popup,height=600,width=800'
      );
    }
  }
}
